/**
 * WhatsNewModal - Displays changelog updates after user login
 * Features: Vinyl record aesthetic, markdown support, version tracking
 */

import { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { VersionAccordion } from './VersionAccordion';
import type { VersionItem } from '../../types/version';

interface WhatsNewModalProps {
  open: boolean;
  onClose: () => void;
  onDismiss: () => void;
  versions: VersionItem[];
  loading?: boolean;
  error?: string;
}

export function WhatsNewModal({ open, onClose, onDismiss, versions, loading, error }: WhatsNewModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      // Delay for entrance animation
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      return undefined;
    }
  }, [open]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center
        bg-black/80 backdrop-blur-md
        transition-opacity duration-500
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `}
      onClick={onClose}
    >
      {/* Vinyl record background effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className={`
            absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            w-[800px] h-[800px] rounded-full
            bg-gradient-radial from-emerald-500/10 via-transparent to-transparent
            transition-all duration-1000 ease-out
            ${isVisible ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}
          `}
          style={{
            background: 'radial-gradient(circle, rgba(16,185,129,0.08) 0%, rgba(16,185,129,0.03) 40%, transparent 70%)',
          }}
        />
        {/* Concentric circles like vinyl grooves */}
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={`
              absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              rounded-full border border-emerald-500/5
              transition-all duration-1000 ease-out
              ${isVisible ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}
            `}
            style={{
              width: `${400 + i * 120}px`,
              height: `${400 + i * 120}px`,
              transitionDelay: `${i * 100}ms`,
            }}
          />
        ))}
      </div>

      {/* Modal card */}
      <div
        className={`
          relative max-w-2xl w-full mx-4
          bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800
          border border-zinc-700/50 rounded-2xl shadow-2xl
          overflow-hidden
          transition-all duration-700 ease-out
          ${isVisible ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-8'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative gradient top bar */}
        <div className="h-1 bg-gradient-to-r from-emerald-600 via-emerald-400 to-emerald-600" />

        {/* Header */}
        <div className="relative px-8 py-6 border-b border-zinc-800/50">
          {/* Sparkle icon */}
          <div className="absolute -top-3 -left-3 w-24 h-24 opacity-10 pointer-events-none">
            <Sparkles className="w-full h-full text-emerald-400" strokeWidth={1} />
          </div>

          <div className="flex items-start justify-between gap-4 relative z-10">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg shadow-emerald-500/20">
                <Sparkles className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white tracking-tight">
                  What's New
                </h2>
                <p className="text-sm text-zinc-400 mt-0.5">
                  Latest updates and features
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="
                flex items-center justify-center w-10 h-10
                text-zinc-400 hover:text-white hover:bg-zinc-800
                rounded-lg transition-all duration-200
                hover:scale-110 active:scale-95
              "
              aria-label="Close"
            >
              <X className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-8 py-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
              <span className="ml-3 text-zinc-400">Loading updates...</span>
            </div>
          )}

          {error && (
            <div className="p-8 text-center">
              <div className="w-12 h-12 text-red-400 mx-auto mb-4">⚠️</div>
              <p className="text-zinc-400">Unable to load version history. Please try again later.</p>
            </div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div className="p-8 text-center">
              <div className="w-12 h-12 text-emerald-400 mx-auto mb-4">✓</div>
              <p className="text-white font-semibold mb-2">You're all caught up! 🎉</p>
              <p className="text-zinc-400">No new updates since your last visit.</p>
            </div>
          )}

          {!loading && !error && versions.length > 0 && (
            <div className="space-y-0">
              {versions.map((version, index) => (
                <VersionAccordion
                  key={version.version}
                  {...version}
                  isFirstUnread={index === 0 && version.isUnread}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-8 py-5 border-t border-zinc-800/50 bg-zinc-900/50 flex items-center justify-between gap-4">
          <button
            onClick={onDismiss}
            className="
              px-4 py-2 text-sm text-zinc-400 hover:text-white
              transition-colors duration-200
            "
          >
            Don't show again
          </button>

          <button
            onClick={onClose}
            className="
              px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-emerald-500
              hover:from-emerald-500 hover:to-emerald-400
              text-white font-medium rounded-lg
              shadow-lg shadow-emerald-500/20
              transition-all duration-200
              hover:scale-105 active:scale-95
            "
          >
            Got it!
          </button>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(39, 39, 42, 0.5);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(82, 82, 91, 0.8);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(113, 113, 122, 0.9);
        }
      `}</style>
    </div>
  );
}
